import { useState } from 'react';
import { CalendarDays } from 'lucide-react';
import { Loader2, X } from './Icons';
import { Modal } from './ModalPortal';
import { useTranslation } from '../contexts/TranslationContext';
import { captureDemoRequestClick } from '../services/analytics/lazyAnalytics';
import { BrandButton } from './ui/BrandButton';
import { demoSchedulingService } from '../services/demoScheduling';

interface DemoRequestCtaProps {
  size?: 'default' | 'compact';
  buttonSize?: 'sm' | 'md' | 'lg';
  className?: string;
  analyticsSource?: string;
}

const DEMO_REQUEST_CTA_CLASS_NAMES = {
  default: 'w-full sm:w-auto sm:px-10 sm:py-5 sm:text-xl',
  compact: 'w-full shadow-none sm:w-auto sm:px-6 sm:py-3 sm:text-base',
};

export function DemoRequestCta({
  size = 'default',
  buttonSize = size === 'compact' ? 'md' : 'lg',
  className = '',
  analyticsSource = 'public_landing_demo_cta',
}: DemoRequestCtaProps) {
  const { t } = useTranslation();
  const [isSchedulingOpen, setIsSchedulingOpen] = useState(false);
  const [isCalendarLoading, setIsCalendarLoading] = useState(true);

  const openSchedulingModal = () => {
    void captureDemoRequestClick({ source: analyticsSource });
    setIsCalendarLoading(true);
    setIsSchedulingOpen(true);
  };

  const closeSchedulingModal = () => {
    setIsSchedulingOpen(false);
    setIsCalendarLoading(true);
  };

  return (
    <>
      <BrandButton
        type="button"
        onClick={openSchedulingModal}
        variant="secondary"
        size={buttonSize}
        className={`${DEMO_REQUEST_CTA_CLASS_NAMES[size]} ${className}`}
      >
        <CalendarDays className={size === 'compact' ? 'h-5 w-5' : 'h-5 w-5 sm:h-6 sm:w-6'} aria-hidden="true" />
        {t('cta.demoButton')}
      </BrandButton>

      <Modal
        isOpen={isSchedulingOpen}
        onClose={closeSchedulingModal}
        labelledBy="demo-request-modal-title"
        panelClassName="flex h-[calc(100dvh-1.5rem)] w-full max-w-6xl flex-col overflow-hidden rounded-lg border border-slate-700/70 bg-slate-900 shadow-2xl sm:h-[calc(100dvh-3rem)]"
      >
        {({ close }) => (
          <>
            <div className="flex items-start justify-between gap-4 border-b border-slate-800/70 px-4 py-4 sm:px-5">
              <div className="min-w-0">
                <h2 id="demo-request-modal-title" className="text-base font-semibold text-slate-100">
                  {t('cta.demoTitle')}
                </h2>
                <p className="mt-1 text-xs leading-relaxed text-slate-300 sm:text-sm">
                  {t('cta.demoDescription')}
                </p>
              </div>
              <button
                type="button"
                onClick={close}
                className="inline-flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg text-slate-300 transition-colors hover:bg-slate-800/70 hover:text-slate-100"
                aria-label={t('cta.demoClose')}
                title={t('cta.demoClose')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="relative min-h-0 flex-1">
              <iframe
                src={demoSchedulingService.getModalUrl()}
                title={t('cta.demoFrameTitle')}
                className="h-full w-full border-0 bg-white"
                loading="lazy"
                onLoad={() => setIsCalendarLoading(false)}
              />
              {isCalendarLoading && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-slate-900"
                  role="status"
                  aria-live="polite"
                  aria-label={t('cta.demoLoading')}
                >
                  <Loader2 className="h-8 w-8 animate-spin text-orange-300" />
                  <span className="text-sm text-slate-200">{t('cta.demoLoading')}</span>
                </div>
              )}
            </div>
          </>
        )}
      </Modal>
    </>
  );
}
